import type {
    Entity,
    ID,
    ResourceResponse,
    ThunkReturnType
} from "types";
import type { PayloadAction, Draft, WritableDraft } from "infrastructure/store";
import { updateTableSliceEntity } from "infrastructure/store/helpers";
import { ApiState } from "infrastructure/api";
import { MenuItem } from "consts/openBanking";
import { compareIds } from "util/support";
import BusinessLogicException from "exceptions/BusinessLogicException";
import type {
    OpenBankingState,
    BankingSdkConnectorsResponse,
    IbanXsBanksResponse,
    BankingSdkConnector,
    IbanXsBank,
    BankingSdkRecipientsResponse,
    BankingSdkRecipient,
    BankingSdkConnectorsRecipientsPivotResponse,
    IbanXsBanksRecipientsPivotResponse,
    IbanXsBanksCountriesPivotResponse
} from "./types";
import initialState from "./state";

export const openBankingResourceRequestLoading = (menuItem: MenuItem) =>
    (state: WritableDraft<OpenBankingState>) => {
        state[menuItem].tableLoadingState = ApiState.Pending;
    };

export const exportOpenBankingResourceLoading = (menuItem: MenuItem) => (
    state: WritableDraft<OpenBankingState>,
    { payload }: PayloadAction<Draft<ApiState>>
) => {
    state[menuItem].exportLoadingState = payload;
};

export const openBankingResourceRequestRejected = (menuItem: MenuItem) =>
    (state: WritableDraft<OpenBankingState>) => {
        state[menuItem].tableLoadingState = ApiState.Failed;
    };

export const createOpenBankingEntityRequestFulfilled = (menuItem: MenuItem) => (
    state: WritableDraft<OpenBankingState>,
    { payload }: PayloadAction<ThunkReturnType<ResourceResponse<Record<PropertyKey, unknown>>>>
) => {
    state[menuItem].tableLoadingState = ApiState.Succeeded;

    if (!state[menuItem].tableSlice || !payload) {
        return;
    }

    switch (menuItem) {
        case MenuItem.BankingSDK: {
            state[menuItem].tableSlice!.data.unshift(payload.data as BankingSdkConnector);

            break;
        }

        case MenuItem.IbanXS: {
            state[menuItem].tableSlice!.data.unshift(payload.data as IbanXsBank);

            break;
        }

        case MenuItem.BankingSDKRecipients: {
            state[menuItem].tableSlice!.data.unshift(payload.data as BankingSdkRecipient);

            break;
        }

        default: {
            break;
        }
    }

    state[menuItem].tableSlice!.to += 1;
    state[menuItem].tableSlice!.total += 1;
};

export const updateOpenBankingEntityRequestFulfilled = <T extends Entity>(menuItem: MenuItem) => (
    state: WritableDraft<OpenBankingState>,
    { payload }: PayloadAction<ThunkReturnType<ResourceResponse<T>>>
) => {
    state[menuItem].tableLoadingState = ApiState.Succeeded;

    const tableSlice = state[menuItem].tableSlice;
    if (!tableSlice || !payload) {
        return;
    }

    switch (menuItem) {
        case MenuItem.BankingSDK: {
            state[menuItem].tableSlice!.data = updateTableSliceEntity(
                state[menuItem],
                payload.data
            );

            break;
        }

        case MenuItem.IbanXS: {
            state[menuItem].tableSlice!.data = updateTableSliceEntity(
                state[menuItem],
                payload.data
            );

            break;
        }

        case MenuItem.BankingSDKRecipients: {
            state[menuItem].tableSlice!.data = updateTableSliceEntity(
                state[menuItem],
                payload.data
            );

            break;
        }

        case MenuItem.BankingSDKConnectorsRecipientsPivot: {
            state[menuItem].tableSlice!.data = updateTableSliceEntity(
                state[menuItem],
                payload.data
            );

            break;
        }

        case MenuItem.IbanXSBanksRecipientsPivot: {
            state[menuItem].tableSlice!.data = updateTableSliceEntity(
                state[menuItem],
                payload.data
            );

            break;
        }

        case MenuItem.IbanXSBanksCountriesPivot: {
            state[menuItem].tableSlice!.data = updateTableSliceEntity(
                state[menuItem],
                payload.data
            );

            break;
        }

        default: {
            throw new BusinessLogicException(`Unknown Open Banking resource of type ${menuItem}`, {
                menuItem,
                payload
            });
        }
    }
};

export const deleteOpenBankingEntityRequestFulfilled = (menuItem: MenuItem) => (
    state: WritableDraft<OpenBankingState>,
    { payload }: PayloadAction<ThunkReturnType<ResourceResponse<ID>>>
) => {
    state[menuItem].tableLoadingState = ApiState.Succeeded;

    const tableSlice = state[menuItem].tableSlice;
    if (!tableSlice) {
        return;
    }

    const data: Entity[] = tableSlice.data;

    state[menuItem].tableSlice!.data = data.filter(({ pki }) =>
        !compareIds(pki, payload!.data)
    ) as typeof tableSlice.data;

    state[menuItem].tableSlice!.to -= 1;
    state[menuItem].tableSlice!.total -= 1;
};

export const bankingSdkConnectorsRequestFulfilled = (
    state: WritableDraft<OpenBankingState>,
    { payload }: PayloadAction<ThunkReturnType<BankingSdkConnectorsResponse>>
) => {
    state[MenuItem.BankingSDK].tableLoadingState = ApiState.Succeeded;
    state[MenuItem.BankingSDK].tableSlice = payload!.data;
};

export const ibanXsBanksRequestFulfilled = (
    state: WritableDraft<OpenBankingState>,
    { payload }: PayloadAction<ThunkReturnType<IbanXsBanksResponse>>) => {
    state[MenuItem.IbanXS].tableLoadingState = ApiState.Succeeded;
    state[MenuItem.IbanXS].tableSlice = payload!.data;
};

export const bankingSdkRecipientsRequestFulfilled = (
    state: WritableDraft<OpenBankingState>,
    { payload }: PayloadAction<ThunkReturnType<BankingSdkRecipientsResponse>>
) => {
    state[MenuItem.BankingSDKRecipients].tableLoadingState = ApiState.Succeeded;
    state[MenuItem.BankingSDKRecipients].tableSlice = payload!.data;
};

export const bankingSdkConnectorsRecipientsPivotRequestFulfilled = (
    state: WritableDraft<OpenBankingState>,
    { payload }: PayloadAction<ThunkReturnType<BankingSdkConnectorsRecipientsPivotResponse>>
) => {
    state[MenuItem.BankingSDKConnectorsRecipientsPivot].tableLoadingState = ApiState.Succeeded;
    state[MenuItem.BankingSDKConnectorsRecipientsPivot].tableSlice = payload!.data;
};

export const ibanXsBanksRecipientsPivotRequestFulfilled = (
    state: WritableDraft<OpenBankingState>,
    { payload }: PayloadAction<ThunkReturnType<IbanXsBanksRecipientsPivotResponse>>
) => {
    state[MenuItem.IbanXSBanksRecipientsPivot].tableLoadingState = ApiState.Succeeded;
    state[MenuItem.IbanXSBanksRecipientsPivot].tableSlice = payload!.data;
};

export const ibanXsBanksCountriesPivotRequestFulfilled = (
    state: WritableDraft<OpenBankingState>,
    { payload }: PayloadAction<ThunkReturnType<IbanXsBanksCountriesPivotResponse>>
) => {
    state[MenuItem.IbanXSBanksCountriesPivot].tableLoadingState = ApiState.Succeeded;
    state[MenuItem.IbanXSBanksCountriesPivot].tableSlice = payload!.data;
};

export const resetState = (menuItem: MenuItem) => (state: WritableDraft<OpenBankingState>) => {
    Object.assign(state[menuItem], initialState[menuItem]);
};
